import {
	type Column,
	type ColumnDef,
	flexRender,
	getCoreRowModel,
	getSortedRowModel,
	type SortingState,
	useReactTable,
} from "@tanstack/react-table";
import {
	ArrowUpDownIcon,
	BadgeCheckIcon,
	CircleHelpIcon,
	FileTextIcon,
	SignalHighIcon,
	SignalLowIcon,
	SignalMediumIcon,
	TableIcon,
	TriangleAlertIcon,
} from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import {
	HoverCard,
	HoverCardContent,
	HoverCardTrigger,
} from "@/components/ui/hover-card";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { captureEvent } from "@/lib/posthog";
import { cn } from "@/lib/utils";

type FactStatus = "confirmed" | "conflict" | "missing";
type FactConfidence = "high" | "medium" | "low";

type FactSourceDocument = {
	fileId: string;
	driveId?: string;
	name: string;
	webUrl?: string;
};

export type FactRegisterTableFact = {
	id: string;
	ordinal: number;
	category: string;
	label: string;
	value: string;
	status: FactStatus;
	confidence: FactConfidence;
	notes?: string;
	sourceDocuments: FactSourceDocument[];
};

function statusVariant(status: FactStatus) {
	if (status === "confirmed") return "secondary";
	if (status === "conflict") return "destructive";
	return "outline";
}

function statusIcon(status: FactStatus) {
	if (status === "confirmed") {
		return <BadgeCheckIcon data-icon="inline-start" aria-hidden="true" />;
	}
	if (status === "conflict") {
		return <TriangleAlertIcon data-icon="inline-start" aria-hidden="true" />;
	}
	return <CircleHelpIcon data-icon="inline-start" aria-hidden="true" />;
}

function confidenceIcon(confidence: FactConfidence) {
	if (confidence === "high") return <SignalHighIcon aria-hidden="true" />;
	if (confidence === "medium") return <SignalMediumIcon aria-hidden="true" />;
	return <SignalLowIcon aria-hidden="true" />;
}

function confidenceLabel(confidence: FactConfidence) {
	if (confidence === "high") return "High";
	if (confidence === "medium") return "Medium";
	return "Low";
}

function statusLabel(status: FactStatus) {
	if (status === "confirmed") return "Verified";
	if (status === "conflict") return "Conflict";
	return "Unresolved";
}

function statusDescription(status: FactStatus) {
	if (status === "confirmed") return "Value matches cited case evidence.";
	if (status === "conflict") {
		return "Cited sources disagree. Human resolution required.";
	}
	return "Reliable supporting value was not found.";
}

function confidenceDescription(confidence: FactConfidence) {
	if (confidence === "high") return "Direct, consistent source support.";
	if (confidence === "medium") return "Partial or indirect source support.";
	return "Weak or incomplete source support.";
}

function StatusBadge({ status }: { status: FactStatus }) {
	return (
		<Tooltip>
			<TooltipTrigger
				render={
					<Badge
						variant={statusVariant(status)}
						aria-label={status}
						className="cursor-help"
					/>
				}
			>
				{statusIcon(status)}
				{statusLabel(status)}
			</TooltipTrigger>
			<TooltipContent side="left" align="center" className="max-w-72">
				<p>
					<span className="font-medium">{statusLabel(status)}.</span>{" "}
					{statusDescription(status)}
				</p>
			</TooltipContent>
		</Tooltip>
	);
}

function ConfidenceBadge({ confidence }: { confidence: FactConfidence }) {
	return (
		<Tooltip>
			<TooltipTrigger
				render={
					<Badge
						variant="outline"
						aria-label={`${confidence} confidence`}
						className="cursor-help"
					/>
				}
			>
				{confidenceIcon(confidence)}
				{confidenceLabel(confidence)}
			</TooltipTrigger>
			<TooltipContent side="left" align="center" className="max-w-72">
				<p>
					<span className="font-medium">
						{confidenceLabel(confidence)} confidence.
					</span>{" "}
					{confidenceDescription(confidence)}
				</p>
			</TooltipContent>
		</Tooltip>
	);
}

const NOTE_PREVIEW_LENGTH = 110;

function EvidenceNote({ notes }: { notes?: string }) {
	const [isTruncated, setIsTruncated] = useState(
		(notes?.length ?? 0) > NOTE_PREVIEW_LENGTH,
	);
	const previewRef = useCallback((preview: HTMLParagraphElement | null) => {
		if (!preview || typeof ResizeObserver === "undefined") return;
		const measure = () =>
			setIsTruncated(preview.scrollHeight > preview.clientHeight);
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(preview);
		return () => observer.disconnect();
	}, []);
	if (!notes) return <span className="text-muted-foreground">—</span>;
	const preview = (
		<p ref={previewRef} className="line-clamp-2 text-xs leading-relaxed">
			{notes}
		</p>
	);
	if (!isTruncated) return preview;
	return (
		<HoverCard openDelay={250} closeDelay={100}>
			<div className="flex min-w-0 items-end gap-1">
				<div className="min-w-0 flex-1">{preview}</div>
				<HoverCardTrigger
					render={
						<Button
							type="button"
							variant="link"
							size="xs"
							className="h-auto shrink-0 p-0"
						/>
					}
				>
					More
				</HoverCardTrigger>
			</div>
			<HoverCardContent side="top" align="start" className="w-80">
				<div className="flex flex-col gap-1.5">
					<p className="font-medium">Full evidence note</p>
					<p className="whitespace-pre-wrap text-sm leading-relaxed">{notes}</p>
				</div>
			</HoverCardContent>
		</HoverCard>
	);
}

function SourceDocumentLink({ document }: { document: FactSourceDocument }) {
	if (!document.webUrl) {
		return (
			<Button
				type="button"
				variant="ghost"
				size="xs"
				disabled
				className="h-6 min-w-0 max-w-full justify-start px-0"
			>
				<FileTextIcon data-icon="inline-start" />
				<span className="truncate">{document.name} · unavailable</span>
			</Button>
		);
	}
	return (
		<a
			href={document.webUrl}
			target="_blank"
			rel="noreferrer"
			title={document.name}
			data-slot="button"
			className={buttonVariants({
				variant: "link",
				size: "xs",
				className: "h-6 min-w-0 max-w-full justify-start px-0",
			})}
			onClick={() =>
				captureEvent("journey.filing_review.document.opened", {
					fileId: document.fileId,
					documentKind: "fact_source",
				})
			}
		>
			<FileTextIcon data-icon="inline-start" />
			<span className="truncate">{document.name}</span>
		</a>
	);
}

function SortableHeader({
	column,
	label,
}: {
	column: Column<FactRegisterTableFact>;
	label: string;
}) {
	return (
		<Button
			variant="ghost"
			size="sm"
			onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
		>
			{label}
			<ArrowUpDownIcon data-icon="inline-end" />
		</Button>
	);
}

function factColumnWidth(columnId: string) {
	if (columnId === "category") return "w-[10%]";
	if (columnId === "label") return "w-[20%]";
	if (columnId === "value") return "w-[25%]";
	if (columnId === "review") return "w-[13%]";
	return "w-[32%]";
}

const factColumns: ColumnDef<FactRegisterTableFact>[] = [
	{
		accessorKey: "category",
		header: ({ column }) => (
			<SortableHeader column={column} label="Case area" />
		),
	},
	{
		accessorKey: "label",
		header: ({ column }) => <SortableHeader column={column} label="Fact" />,
		cell: ({ getValue }) => (
			<span className="font-medium">{getValue<string>()}</span>
		),
	},
	{
		accessorKey: "value",
		header: ({ column }) => (
			<SortableHeader column={column} label="Recorded value" />
		),
		cell: ({ getValue }) => getValue<string>() || "—",
	},
	{
		id: "review",
		accessorFn: (fact) => (fact.status === "confirmed" ? 0 : 1),
		header: ({ column }) => (
			<SortableHeader column={column} label="Verification" />
		),
		cell: ({ row }) => (
			<div className="flex flex-wrap items-center gap-1">
				<StatusBadge status={row.original.status} />
				<ConfidenceBadge confidence={row.original.confidence} />
			</div>
		),
	},
	{
		id: "evidence",
		header: "Basis & sources",
		enableSorting: false,
		cell: ({ row }) => (
			<div className="flex min-w-0 flex-col gap-1.5">
				<EvidenceNote notes={row.original.notes} />
				<div className="flex min-w-0 flex-col items-start gap-0.5">
					{row.original.sourceDocuments.map((document) => (
						<SourceDocumentLink
							key={`${document.driveId}:${document.fileId}`}
							document={document}
						/>
					))}
				</div>
			</div>
		),
	},
];

export function FactRegisterTable({
	facts,
	expanded = false,
}: {
	facts: FactRegisterTableFact[];
	expanded?: boolean;
}) {
	const [sorting, setSorting] = useState<SortingState>([
		{ id: "review", desc: true },
	]);
	const table = useReactTable({
		data: facts,
		columns: factColumns,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		onSortingChange: setSorting,
		state: { sorting },
	});

	return (
		<ScrollArea
			className={cn(
				"w-full",
				expanded
					? "[&_[data-slot=scroll-area-viewport]]:max-h-[70vh]"
					: "[&_[data-slot=scroll-area-viewport]]:max-h-[32rem]",
			)}
		>
			<Table className="w-full table-fixed" scrollable={false}>
				<TableHeader>
					{table.getHeaderGroups().map((headerGroup) => (
						<TableRow key={headerGroup.id}>
							{headerGroup.headers.map((header) => (
								<TableHead
									key={header.id}
									className={cn(
										"sticky top-0 bg-card",
										factColumnWidth(header.column.id),
									)}
								>
									{header.isPlaceholder
										? null
										: flexRender(
												header.column.columnDef.header,
												header.getContext(),
											)}
								</TableHead>
							))}
						</TableRow>
					))}
				</TableHeader>
				<TableBody>
					{table
						.getRowModel()
						.rows.slice(0, expanded ? undefined : 8)
						.map((row) => (
							<TableRow key={row.original.id}>
								{row.getVisibleCells().map((cell) => (
									<TableCell
										key={cell.id}
										className="min-w-0 align-top whitespace-normal break-words"
									>
										{flexRender(cell.column.columnDef.cell, cell.getContext())}
									</TableCell>
								))}
							</TableRow>
						))}
				</TableBody>
			</Table>
		</ScrollArea>
	);
}

type FactRegisterArtifact = {
	fileId?: string;
	webUrl?: string;
};

export function FactRegisterContent({
	factRegisterId,
	version,
	artifact,
	facts,
	defaultCollapsed = false,
}: {
	factRegisterId: string;
	version: number;
	artifact?: FactRegisterArtifact;
	facts: FactRegisterTableFact[];
	/** Superseded versions start compact; expanding reveals the table. */
	defaultCollapsed?: boolean;
}) {
	const [expanded, setExpanded] = useState(false);
	const [collapsed, setCollapsed] = useState(defaultCollapsed);
	const wasSuperseded = useRef(defaultCollapsed);
	if (defaultCollapsed && !wasSuperseded.current) {
		wasSuperseded.current = true;
		setCollapsed(true);
	} else if (!defaultCollapsed) {
		wasSuperseded.current = false;
	}
	const needsAttention = facts.filter(
		(fact) => fact.status !== "confirmed",
	).length;
	if (defaultCollapsed && collapsed) {
		return (
			<div
				id={`fact-register-${factRegisterId}`}
				className="my-2 flex items-center gap-2 rounded-lg border bg-muted/40 px-3 py-1.5 text-xs text-muted-foreground"
			>
				<span className="font-semibold text-foreground">v{version}</span>
				<span aria-hidden="true">·</span>
				<span>{facts.length} facts</span>
				<span aria-hidden="true">·</span>
				<span>{needsAttention} needs attention</span>
				<span className="flex-1" />
				<Button
					type="button"
					variant="ghost"
					size="xs"
					aria-expanded={false}
					onClick={() => setCollapsed(false)}
				>
					<TableIcon data-icon="inline-start" />
					Show
				</Button>
			</div>
		);
	}
	return (
		<div
			id={`fact-register-${factRegisterId}`}
			className="my-2 overflow-hidden rounded-xl border bg-card/60"
		>
			<div
				className={cn(
					"flex items-center justify-between gap-3 px-4 py-2.5",
					collapsed ? "" : "border-b",
				)}
			>
				<div className="flex flex-col gap-1">
					<div className="flex items-center gap-2">
						<span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
							Fact Register
						</span>
						<Badge variant="secondary">v{version}</Badge>
						{defaultCollapsed ? (
							<Badge variant="outline">superseded</Badge>
						) : null}
					</div>
					<p className="text-xs text-muted-foreground">
						{facts.length} facts · {needsAttention} needs attention
					</p>
				</div>
				<div className="flex items-center gap-2">
					{artifact?.webUrl ? (
						<a
							href={artifact.webUrl}
							target="_blank"
							rel="noreferrer"
							data-slot="button"
							className={buttonVariants({
								variant: "secondary",
								size: "sm",
							})}
							onClick={() =>
								captureEvent("journey.filing_review.document.opened", {
									fileId: artifact.fileId,
									documentKind: "fact_register",
								})
							}
						>
							<FileTextIcon data-icon="inline-start" />
							Open DOCX
						</a>
					) : null}
					{defaultCollapsed ? (
						<Button
							type="button"
							variant="ghost"
							size="sm"
							aria-expanded={!collapsed}
							onClick={() => setCollapsed((current) => !current)}
						>
							{collapsed ? `Show ${facts.length} facts` : "Hide facts"}
						</Button>
					) : null}
				</div>
			</div>
			{collapsed ? null : (
				<>
					<FactRegisterTable facts={facts} expanded={expanded} />
					{facts.length > 8 ? (
						<div className="flex justify-center border-t p-2">
							<Button
								type="button"
								variant="secondary"
								size="sm"
								aria-expanded={expanded}
								onClick={() => setExpanded((current) => !current)}
							>
								{expanded ? "Collapse" : `Expand all ${facts.length} facts`}
							</Button>
						</div>
					) : null}
				</>
			)}
		</div>
	);
}
